if (!customElements.get("product-card")) {
  class ProductCard extends HTMLElement {
    constructor() {
      super();
      this.activeVariant = null;

      // bind once so add/removeEventListener works reliably
      this._preventClick = this._preventClick.bind(this);
    }

    connectedCallback() {
      this.initColorSwitcher();
      this.setInitialVariant();
      this.updateProductInfo();
    }

    getProductData() {
      const productDataEl = this.querySelector(".product-json");
      if (!productDataEl) return null;

      try {
        return JSON.parse(productDataEl.innerHTML);
      } catch (e) {
        console.error("Invalid JSON in product-card:", e);
        return null;
      }
    }

    getSelectedValues() {
      const optionDivs = this.querySelectorAll(".product-card__options");
      return Array.from(optionDivs).map((optionDiv) => {
        const checked = optionDiv.querySelector("input:checked");
        return checked ? checked.value : null;
      });
    }

    setInitialVariant() {
      const productData = this.getProductData();
      if (!productData?.variants?.length) return;

      if (productData.default_variant_id != null) {
        const preferred = productData.variants.find(
          (v) => Number(v.id) === Number(productData.default_variant_id)
        );
        if (preferred) {
          this.setActiveVariantAndSyncUI(preferred);
          return;
        }
      }

      const selectedValues = this.getSelectedValues();

      let variant =
        productData.variants.find(
          (v) => v.available && v.options.every((opt, i) => selectedValues[i] === opt)
        ) ||
        productData.variants.find((v) => v.options.every((opt, i) => selectedValues[i] === opt)) ||
        productData.variants.find((v) => v.available) ||
        productData.variants[0];

      this.setActiveVariantAndSyncUI(variant);
    }

    initColorSwitcher() {
      const colorInputs = this.querySelectorAll('[name^="color-"]');
      colorInputs.forEach((input) => {
        input.addEventListener("change", () => {
          this.onSetActiveVariant();
          this.updateProductInfo();
        });
      });
    }

    onSetActiveVariant() {
      const productData = this.getProductData();
      if (!productData?.variants?.length) return;

      const selectedValues = this.getSelectedValues();

      let exactAvailable = productData.variants.find((v) => {
        return v.available && v.options.every((opt, i) => selectedValues[i] === opt);
      });

      if (exactAvailable) {
        this.setActiveVariantAndSyncUI(exactAvailable);
        return;
      }

      let exactAny = productData.variants.find((v) => {
        return v.options.every((opt, i) => selectedValues[i] === opt);
      });

      if (exactAny?.available === false) {
        const nonNullMatch = (v) =>
          v.options.every((opt, i) => selectedValues[i] == null || selectedValues[i] === opt);

        const firstAvailableClose = productData.variants.find((v) => v.available && nonNullMatch(v));

        if (firstAvailableClose) {
          this.setActiveVariantAndSyncUI(firstAvailableClose);
          return;
        }

        this.activeVariant = exactAny;
        this.emitVariantChange(this.activeVariant);
        return;
      }

      if (exactAny) {
        this.activeVariant = exactAny;
        this.emitVariantChange(this.activeVariant);
        return;
      }
    }

    setActiveVariantAndSyncUI(variant) {
      if (!variant) return;

      this.activeVariant = variant;

      const optionDivs = this.querySelectorAll(".product-card__options");
      optionDivs.forEach((div, index) => {
        const valueToCheck = variant.options[index];
        const inputToCheck = div.querySelector(`input[value="${CSS.escape(valueToCheck)}"]`);
        if (inputToCheck) inputToCheck.checked = true;
      });

      this.emitVariantChange(variant);
    }

    emitVariantChange(variant) {
      this.dispatchEvent(
        new CustomEvent("variant:change", {
          bubbles: true,
          detail: { variant },
        })
      );
    }

    updateProductInfo() {
      this.updateImage(this.activeVariant);
      this.updatePrices(this.activeVariant);
      this.updateLinks(this.activeVariant);
      this.updateIDInputs(this.activeVariant);
      this.updateActionButtons(this.activeVariant);
      this.updateSelectedOptionValues(this.activeVariant);
    }

    updateImage(activeVariant) {
      const cardImage = this.querySelector(".product-card__picture img");
      if (cardImage && activeVariant?.featured_image) {
        cardImage.src = activeVariant.featured_image;
        cardImage.alt = activeVariant.featured_image_alt || "";
      }
    }

    updatePrices(activeVariant) {
      const currentPriceEl = this.querySelector(".product-card__current-price");
      const originalPriceEl = this.querySelector(".product-card__original-price");
      const productData = this.getProductData();
      const promoEnabled = productData?.promo_pricing_enabled === true;

      if (promoEnabled && activeVariant?.promo_sale_price) {
        if (currentPriceEl) currentPriceEl.textContent = activeVariant.promo_sale_price;
        if (originalPriceEl) {
          const multiplier = Number(productData?.promo_multiplier || 100);
          const priceCents = Number(activeVariant.shopify_price || 0);
          const compareCents = Number(activeVariant.shopify_compare_at_price || 0);
          const saleCents = Math.floor((priceCents * multiplier) / 100);
          const compareText =
            compareCents > saleCents && compareCents > priceCents && activeVariant.compare_at_price
              ? activeVariant.compare_at_price
              : activeVariant.promo_full_price;

          originalPriceEl.textContent = compareText;
          originalPriceEl.classList.remove("hidden");
        }
        return;
      }

      if (currentPriceEl) currentPriceEl.textContent = activeVariant?.price ?? "";

      if (originalPriceEl) {
        const price = parseFloat((activeVariant?.price ?? "").replace(/[^0-9.]/g, ""));
        const compareAtPrice =
          activeVariant?.compare_at_price &&
          parseFloat(activeVariant.compare_at_price.replace(/[^0-9.]/g, ""));

        if (activeVariant?.compare_at_price && compareAtPrice > price) {
          originalPriceEl.textContent = activeVariant.compare_at_price;
          originalPriceEl.classList.remove("hidden");
        } else {
          originalPriceEl.classList.add("hidden");
        }
      }
    }

    updateLinks(activeVariant) {
      const productLinks = this.querySelectorAll(".product-card__link");
      productLinks.forEach((link) => {
        link.href = activeVariant?.url || "#";
      });
    }

    updateIDInputs(activeVariant) {
      const variantInputs = this.querySelectorAll(".product-variant-id");
      variantInputs.forEach((input) => {
        input.value = activeVariant?.id ?? "";
      });
    }

    updateActionButtons(activeVariant) {
      const isSoldOut = activeVariant?.available === false;

      const buyNow = this.querySelector(".product-card__btn-link");
      if (buyNow) {
        if (!buyNow.dataset.originalText) buyNow.dataset.originalText = buyNow.textContent.trim();

        if (isSoldOut) {
          buyNow.textContent = window.variantStrings?.soldOut || "SOLD OUT";
          buyNow.setAttribute("aria-disabled", "true");
          buyNow.setAttribute("tabindex", "-1");
          buyNow.classList.add("is-disabled");
          buyNow.addEventListener("click", this._preventClick, { passive: false });
        } else {
          buyNow.textContent = buyNow.dataset.originalText;
          buyNow.removeAttribute("aria-disabled");
          buyNow.removeAttribute("tabindex");
          buyNow.classList.remove("is-disabled");
          buyNow.removeEventListener("click", this._preventClick);
        }
      }

      const quickAddBtns = this.querySelectorAll(".product-card__quick-add-btn");
      quickAddBtns.forEach((btn) => {
        let textEl = btn.querySelector(".product-card__quick-add-text");
        if (!textEl) {
          textEl = document.createElement("span");
          textEl.className = "product-card__quick-add-text";
          textEl.textContent = "ADD TO CART";
          btn.prepend(textEl);
        }

        if (!btn.dataset.originalText) btn.dataset.originalText = textEl.textContent.trim();

        if (isSoldOut) {
          btn.disabled = true;
          textEl.textContent = window.variantStrings?.soldOut || "SOLD OUT";
          btn.classList.add("is-disabled");
        } else {
          btn.disabled = false;
          textEl.textContent = btn.dataset.originalText;
          btn.classList.remove("is-disabled");
        }
      });
    }

    _preventClick(e) {
      e.preventDefault();
      e.stopPropagation();
    }

    updateSelectedOptionValues(activeVariant) {
      const outputs = this.querySelectorAll("[data-selected-value][data-option-position]");
      if (!outputs.length) return;

      outputs.forEach((el) => {
        const pos = parseInt(el.dataset.optionPosition, 10);
        const idx = pos - 1;
        const value = activeVariant?.options?.[idx];

        el.textContent = value ? value : "";
      });
    }

  }

  customElements.define("product-card", ProductCard);
}
